import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { money, num } from "@owners/format";
import { api } from "../api";

/**
 * Awaiting Approval (demo artboard; Academy feed, Genesis 11/02). Purchase
 * orders over $25,000 reach ownership after corporate final approval. Owners
 * and Asset Managers approve, decline or ask the GM a question; the decision
 * goes to Academy (approve sends the usual accountant / Regional emails;
 * question returns it to the GM as "More info needed"). A note is required to
 * decline or question. Admins see the queue read-only.
 */

interface Po { id: string; type: string | null; title: string; description: string | null; amount: number | null; vendor: string | null;
  submitted_by: string | null; submitted_at: string | null; link: string | null }
interface Res { connected: boolean; canDecide: boolean; total: number; hotels: Array<{ id: string; name: string; unavailable: boolean; approvals: Po[] }> }

const day = (s: string | null) => (s ? new Date(s.length === 10 ? s + "T12:00:00" : s).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "");

export function AwaitingApproval() {
  const q = useQuery({ queryKey: ["approvals"], queryFn: () => api<Res>("/api/approvals?hotelIds=all"), refetchInterval: 120_000 });
  const d = q.data;
  const items = (d?.hotels ?? []).flatMap((h) => h.approvals.map((po) => ({ po, hotel: h.name })));
  const total = items.reduce((t, x) => t + (x.po.amount ?? 0), 0);
  const down = (d?.hotels ?? []).filter((h) => h.unavailable).map((h) => h.name);

  return (
    <>
      <div className="eyebrow">Owners Module · Awaiting Approval</div>
      <h1>Awaiting your approval</h1>
      <p className="muted" style={{ marginTop: -6 }}>Purchase orders over $25,000 come to you after corporate final approval.</p>

      {q.isLoading ? <div className="card muted">Loading…</div> : q.error ? <div className="card error">{(q.error as Error).message}</div> : !d?.connected ? (
        <div className="card muted">Academy isn't connected yet, so there's nothing to approve here.</div>
      ) : (
        <>
          <div className="tiles strip">
            <div className="card tile"><div className="eyebrow">Waiting on you</div><div className="tile-value">{num(items.length)}</div><div className="tile-sub">purchase orders</div></div>
            <div className="card tile"><div className="eyebrow">Total</div><div className="tile-value">{money(total, { decimals: 0 })}</div><div className="tile-sub">across {num(new Set(items.map((x) => x.hotel)).size)} hotels</div></div>
          </div>
          {!d.canDecide && <p className="muted">You can see the queue; owners and asset managers signed in as themselves make the decisions.</p>}
          {down.length > 0 && <p className="error" style={{ fontSize: 13 }}>Academy didn't answer for {down.join(", ")} — try again in a few minutes.</p>}
          {items.length === 0 ? <div className="card muted">Nothing is waiting on you right now.</div> : (
            <div className="po-list">{items.map(({ po, hotel }) => <PoCard key={po.id} po={po} hotel={hotel} canDecide={d.canDecide} />)}</div>
          )}
        </>
      )}
    </>
  );
}

function PoCard({ po, hotel, canDecide }: { po: Po; hotel: string; canDecide: boolean }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<null | "decline" | "question">(null);
  const [note, setNote] = useState("");
  const [done, setDone] = useState<string | null>(null);
  const send = useMutation({
    mutationFn: (b: { decision: "approve" | "decline" | "question"; note?: string }) => api(`/api/approvals/${encodeURIComponent(po.id)}/decision`, { method: "POST", body: b }),
    onSuccess: (_r, b) => {
      setDone(b.decision === "approve" ? "Approved — Academy is notifying accounting and the Regional." : b.decision === "decline" ? "Declined." : "Sent back to the GM for more information.");
      setTimeout(() => { qc.invalidateQueries({ queryKey: ["approvals"] }); qc.invalidateQueries({ queryKey: ["nav-counts"] }); }, 1500);
    },
  });
  const approve = () => { if (confirm(`Approve ${po.title}${po.amount != null ? ` for ${money(po.amount, { decimals: 2 })}` : ""}?`)) send.mutate({ decision: "approve" }); };

  return (
    <div className="card po-card">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="row" style={{ gap: 8 }}>
            {po.type && <span className="need-tag">{po.type}</span>}
            <span className="muted" style={{ fontSize: 12 }}>{hotel}</span>
          </div>
          <h3 style={{ margin: "4px 0" }}>{po.title}</h3>
          {po.description && <p style={{ margin: "2px 0 6px", fontSize: 14 }}>{po.description}</p>}
          <div className="muted" style={{ fontSize: 12 }}>
            {po.vendor ? `${po.vendor} · ` : ""}Submitted{po.submitted_by ? ` by ${po.submitted_by}` : ""}{po.submitted_at ? ` on ${day(po.submitted_at)}` : ""}
            {po.link && <> · <a href={po.link} target="_blank" rel="noreferrer">Open the PO in Academy</a></>}
          </div>
        </div>
        <div className="po-amount">{po.amount != null ? money(po.amount, { decimals: 0 }) : "—"}</div>
      </div>

      {done ? <p className="ok" style={{ margin: "10px 0 0" }}>{done}</p> : canDecide && (
        <>
          {send.error && <p className="error" style={{ margin: "8px 0 0" }}>{(send.error as Error).message}</p>}
          {mode ? (
            <div style={{ marginTop: 10 }}>
              <textarea rows={3} style={{ width: "100%" }} value={note} maxLength={2000} autoFocus
                placeholder={mode === "decline" ? "Why you're declining (the GM and corporate see this)" : "Your question for the GM"} onChange={(e) => setNote(e.target.value)} />
              <div className="row" style={{ marginTop: 6 }}>
                <button className={`btn${mode === "decline" ? " danger" : ""}`} disabled={!note.trim() || send.isPending}
                  onClick={() => send.mutate({ decision: mode, note: note.trim() })}>{mode === "decline" ? "Decline" : "Send question"}</button>
                <button className="btn secondary" onClick={() => { setMode(null); setNote(""); }}>Cancel</button>
              </div>
            </div>
          ) : (
            <div className="row" style={{ marginTop: 10 }}>
              <button className="btn" disabled={send.isPending} onClick={approve}>Approve</button>
              <button className="btn secondary" disabled={send.isPending} onClick={() => setMode("decline")}>Decline</button>
              <button className="btn secondary" disabled={send.isPending} onClick={() => setMode("question")}>Ask a question</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
